import getDecorators from "inversify-inject-decorators";
import { SimpleGame } from "../../../app";
import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";
import { DefaultEventService } from "../../../services/event-service";
import { SaveGameService } from "../../../services/save-game-service";
import { ChoiceDialogContentDTO, ChoiceDTO, GameStateDTO } from "../../../shared/dtos";
import { Direction, SceneId, SocketEvents } from "../../../shared/enum";
import { DefaultHorizontalMenu } from "../../components/base-menu";
import { LoadingGameCard } from "../../components/title-screen/loading-game-card";
import { ThemeManager } from "../../design-system/theme-manager";
import { ConfirmationDialog } from "../../menu/dialogs/confirmation-dialog";
import { AbstractMapScene } from "../../scenes/maps/abstract-map-scene";
import { StyleOptions } from "../interfaces";

const { lazyInject } = getDecorators(container);

export class LoadGameMenu extends DefaultHorizontalMenu<LoadingGameCard> {
    @lazyInject(TYPES.EventService)
    private eventService: DefaultEventService;

    @lazyInject(TYPES.ThemeManager)
    private themeManager: ThemeManager;

    @lazyInject(TYPES.SaveGameService)
    private saveGameService: SaveGameService;

    private currentPage: number = 0;
    private slotsPerPage: number;
    private allSlots: Array<GameStateDTO>;
    private totalPages: number;
    private onPageChangeCallback: (page: number, totalPages: number) => void;

    // Dialog state for delete confirmation
    private deleteConfirmationDialog: ConfirmationDialog | null = null;
    private pendingDeleteSaveId: string | null = null;

    // Screen margin for viewport calculation
    private static readonly SCREEN_MARGIN = 40;

    constructor(
        x: number,
        y: number,
        width: number,
        height: number,
        loadedGames: Array<GameStateDTO>,
        scene: Phaser.Scene
    ) {
        super(scene, x, y);
        this.scene = scene;

        // Calculate how many slots can fit on screen
        this.calculateSlotsPerPage();

        // Store all slots
        this.allSlots = [...loadedGames];

        // Calculate total pages
        this.calculatePagination();

        // Render first page
        this.renderCurrentPage();

        this.eventService.register(SocketEvents.LOAD_GAME_REQUEST_ACK, () => this.onLoaded());
    }

    private calculateSlotsPerPage() {
        const spacing = this.themeManager.getSpacing("xl");
        const availableWidth = this.scene.cameras.main.width - LoadGameMenu.SCREEN_MARGIN * 2;
        const slotWidth = LoadingGameCard.CARD_WIDTH + spacing;
        const maxVisibleSlots = Math.floor(availableWidth / slotWidth);

        // Ensure at least 1 slot visible
        this.slotsPerPage = Math.max(1, maxVisibleSlots);
    }

    private calculatePagination() {
        // Handle empty save list
        if (this.allSlots.length === 0) {
            this.totalPages = 1;
            this.currentPage = 0;
            return;
        }

        this.totalPages = Math.ceil(this.allSlots.length / this.slotsPerPage);
        // Clamp current page
        this.currentPage = Math.max(0, Math.min(this.currentPage, this.totalPages - 1));
    }

    private getVisibleSlots(): Array<GameStateDTO> {
        const startIndex = this.currentPage * this.slotsPerPage;
        const endIndex = startIndex + this.slotsPerPage;
        return this.allSlots.slice(startIndex, endIndex);
    }

    private renderCurrentPage() {
        // Clear existing items
        this.clearMenuItems();

        // Add visible slots for current page
        const visibleSlots = this.getVisibleSlots();
        visibleSlots.forEach((loadGame: GameStateDTO) => {
            this.addMenuItem(
                new LoadingGameCard(
                    LoadingGameCard.CARD_WIDTH,
                    LoadingGameCard.CARD_HEIGHT,
                    loadGame,
                    this.scene
                )
            );
        });

        // Layout items
        const spacing = this.themeManager.getSpacing("xl");
        this.centerElementsX({ spaceBetween: spacing } as StyleOptions);
        this.centerElementsY();

        // Select first item
        if (this.menuItems.length > 0) {
            this.menuItems[0].select();
        }

        // Notify parent of page change
        if (this.onPageChangeCallback) {
            this.onPageChangeCallback(this.currentPage, this.totalPages);
        }
    }

    private clearMenuItems() {
        // Destroy existing menu items
        this.menuItems.forEach((item) => {
            this.remove(item);
            item.destroy();
        });
        this.menuItems.length = 0;
        this._menuItemIndex = 0;
    }

    public nextPage() {
        if (this.currentPage >= this.totalPages - 1) return;

        this.currentPage++;
        this.refreshPage();
    }

    public previousPage() {
        if (this.currentPage <= 0) return;

        this.currentPage--;
        this.refreshPage();
    }

    private refreshPage() {
        this.renderCurrentPage();

        // Optional: Add fade transition
        this.alpha = 0;
        this.scene.tweens.add({
            targets: this,
            alpha: 1,
            duration: 200,
        });
    }

    public setPageChangeCallback(callback: (page: number, totalPages: number) => void) {
        this.onPageChangeCallback = callback;
        // Trigger initial callback
        if (callback) {
            callback(this.currentPage, this.totalPages);
        }
    }

    public canGoToPreviousPage(): boolean {
        return this.currentPage > 0;
    }

    public canGoToNextPage(): boolean {
        return this.currentPage < this.totalPages - 1;
    }

    public getCurrentPage(): number {
        return this.currentPage;
    }

    public getTotalPages(): number {
        return this.totalPages;
    }

    // Override keyboard input to handle page navigation and delete
    public onKeyInput(event: KeyboardEvent) {
        // If dialog is open, handle dialog navigation only
        if (this.deleteConfirmationDialog) {
            if (event.code === "ArrowLeft") {
                this.deleteConfirmationDialog.changeSelection(Direction.LEFT);
            } else if (event.code === "ArrowRight") {
                this.deleteConfirmationDialog.changeSelection(Direction.RIGHT);
            } else if (event.code === "Space") {
                this.deleteConfirmationDialog.onAction();
            }
            return;
        }

        // Normal menu navigation
        if (event.code === "ArrowRight") {
            // If at rightmost item, try to go to next page
            if (this._menuItemIndex >= this.menuItems.length - 1 && this.canGoToNextPage()) {
                this.nextPage();
            } else {
                this.moveSelectionRight();
            }
        } else if (event.code === "ArrowLeft") {
            // If at leftmost item, try to go to previous page
            if (this._menuItemIndex <= 0 && this.canGoToPreviousPage()) {
                this.previousPage();
            } else {
                this.moveSelectionLeft();
            }
        } else if (event.code === "Space") {
            this.confirm();
        } else if (event.code === "Delete") {
            this.requestDeleteCurrentSave();
        }
    }

    public draw() {
        const graphics = this.scene.add.graphics();
        //@ts-ignore
        const menu = this.scene.add.container();
        this.menuItems.forEach((item) => menu.add(item));
    }

    public confirm() {
        // Calculate the actual slot index across all pages
        const actualIndex = this.currentPage * this.slotsPerPage + this.menuItemIndex;
        const gameState = this.allSlots[actualIndex];

        SimpleGame.CURRENT_GAME = gameState.save_id; // Changed from game_id
        this.eventService.emit(SocketEvents.LOAD_GAME_REQUEST, SimpleGame.CURRENT_GAME);
    }

    private onLoaded() {
        // Calculate the actual slot index across all pages
        const actualIndex = this.currentPage * this.slotsPerPage + this.menuItemIndex;
        const gameState = this.allSlots[actualIndex];

        //        this.scene.scene.start(gameState.game_location.scene_id);
        this.scene.scene.add(
            gameState.game_location.scene_id,
            new AbstractMapScene(gameState.game_location.scene_id),
            false,
            { sceneId: gameState.game_location.scene_id }
        );
        this.scene.scene.start(gameState.game_location.scene_id, {
            sceneId: gameState.game_location.scene_id,
        });
    }

    private requestDeleteCurrentSave() {
        // Calculate the actual slot index across all pages
        const actualIndex = this.currentPage * this.slotsPerPage + this.menuItemIndex;
        const loadGame = this.allSlots[actualIndex];

        // Should always have a valid save in LoadGameMenu
        if (!loadGame) {
            return;
        }

        // Store pending delete save ID
        this.pendingDeleteSaveId = loadGame.save_id;

        // Show confirmation dialog
        const dialogContent = new ChoiceDialogContentDTO();
        dialogContent.message = `Delete "${loadGame.save_name}"?`;
        dialogContent.choices = [
            Object.assign(new ChoiceDTO(), { choice: "Yes", next: [] }),
            Object.assign(new ChoiceDTO(), { choice: "No", next: [] }),
        ];
        dialogContent.wait_for_player_input = true;

        this.deleteConfirmationDialog = new ConfirmationDialog(this.scene, dialogContent);
        this.deleteConfirmationDialog.draw();
        this.deleteConfirmationDialog.setText(dialogContent.message);

        // Listen for dialog choice on the scene's event system
        this.scene.events.once("dialog_choice", (selection: number) => {
            this.onDeleteConfirmation(selection);
        });
    }

    private async onDeleteConfirmation(selection: number) {
        // Clean up dialog reference (scene will clean up the container)
        if (this.deleteConfirmationDialog) {
            this.deleteConfirmationDialog = null;
        }

        // If user selected "No" (index 1), do nothing
        if (selection !== 0) {
            this.pendingDeleteSaveId = null;
            return;
        }

        // User selected "Yes", proceed with deletion
        if (this.pendingDeleteSaveId === null) {
            return;
        }

        try {
            // Delete save via backend
            await this.saveGameService.deleteSave(this.pendingDeleteSaveId);

            // Remove from array
            this.allSlots = this.allSlots.filter(
                (save) => save.save_id !== this.pendingDeleteSaveId
            );

            // If no saves remain, return to title screen
            if (this.allSlots.length === 0) {
                this.scene.scene.start(SceneId.TITLE_SCREEN);
                return;
            }

            // Recalculate pagination
            this.calculatePagination();

            // If current page is now empty, go to previous page
            if (this.currentPage > 0 && this.currentPage >= this.totalPages) {
                this.currentPage = this.totalPages - 1;
            }

            // Refresh the page
            this.refreshPage();
        } catch (error) {
            console.error("Failed to delete save:", error);
            this.showError("Delete Failed", (error as Error).message);
        } finally {
            this.pendingDeleteSaveId = null;
        }
    }

    private showError(title: string, message: string) {
        // Simple error display - can be improved with a proper dialog component later
        const errorText = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.centerY,
            `${title}\n\n${message}\n\nPress SPACE to dismiss.`,
            {
                fontSize: "18px",
                color: "#ff0000",
                fontFamily: "monospace",
                align: "center",
                backgroundColor: "#000000",
                padding: { x: 20, y: 20 },
            }
        );
        errorText.setOrigin(0.5);
        errorText.setDepth(1000);

        // Dismiss on SPACE key
        const dismissHandler = (event: KeyboardEvent) => {
            if (event.code === "Space") {
                errorText.destroy();
                this.scene.input.keyboard?.off("keydown", dismissHandler);
            }
        };
        this.scene.input.keyboard?.on("keydown", dismissHandler);
    }

    public destroy() {
        // Clean up dialog reference if still open (scene will clean up the container)
        if (this.deleteConfirmationDialog) {
            this.deleteConfirmationDialog = null;
        }

        // Remove any pending dialog choice listeners
        this.scene.events.off("dialog_choice");

        // Clean up event listeners
        this.eventService.off(SocketEvents.LOAD_GAME_REQUEST_ACK);
        super.destroy();
    }
}
